import { booleanDataSocket, numericDataSocket, numericNumberDataSocket } from "../socket_types"
import { BaseComponent } from "./base_component"
import { BinaryOpComponent } from "./binary_op_component"
import { MapLayerComponent, SaveMapLayer } from "./map_layer_component"
import { NumericConstantComponent } from "./numeric_constant_component"
import { UnaryOpComponent } from "./unary_op_component"
import { VariadicOpComponent } from "./variadic_op_component"
import { MaskNumericDataComponent } from "./mask_numeric_data_component"
import { NumberToNumericDatasetComponent } from "./number_to_numeric_dataset_component"
import { NevoLayerComponent } from "./nevo_layer_component"
import { OSMLandUseComponent } from "./osm_land_use_component"
import { AreaComponent } from "./area_component"
import { BarChartComponent } from "./bar_chart_component"
import {
  BAR_DISTRIBUTION_CHART_COMPONENT_NAME,
  DatasetChartComponent,
  HISTOGRAM_CHART_COMPONENT_NAME,
  PIE_CHART_COMPONENT_NAME,
  SpiderChartComponent,
  TableChartComponent,
  TableCompareChartComponent,
} from "./dataset_chart_component"
import { DistanceMapComponent } from "./distance_map_component"
import { CategoricalComponent } from "./categorical_component"
import { DigitalModelComponent } from "./digital_model_component"
import { ExpressionComponent, getExpressionsType } from "./expression_component"
import { NumericDatasetToNumberComponent } from "./numeric_dataset_to_numeric_component"
import { SaveModelOutputComponent, SaveModel } from "./save_model_component"
import { PrecompiledModelComponent, getDatasets } from "./dataset_component"
import { UkcehLandCoverComponent } from "./ukceh_land_cover_component"
import { CensusComponent } from "./census_component"
import { OSGreenSpacesComponent } from "./os_greenspaces_component"
import { CellAreaComponent } from "./cell_area_component"
import { ScaleFactorComponent } from "./scale_factor_component"
import { CROMEComponent } from "./crome_component"
import { RescaleComponent } from "./rescale_component"
import { Extent } from "ol/extent"
import { ReplaceNaNComponent } from "./replace_nan_component"
import { BiodiversityComponent } from "./biodiversity_component"
import { LehLandCoverComponent } from "./leh_land_cover_component"
import { ATIComponent } from "./ati_component"
import { DesignationsComponent } from "./designations_component"
import { ORValComponent } from "./orval_component"
import { IMDComponent } from "./imd_component"
import { HedgerowComponent } from "./hedgerow_component"
import { ProjectPermissions } from "../../editor/project_editor"
import { SoilComponent } from "./soil_component"
import { SegmentComponent } from "./segment_component"
import { KewSamplesComponent } from "./kew_samples_component"
import { InterpolationComponent } from "./interpolation_component"
import { SlopeComponent } from "./slope_component"
import { NatmapSoilComponent } from "./natmap_soil_component"
import { UnitComponent } from "./units_component"
import { areas, units } from "../tile_grid"
import { KewTreesComponent } from "./kew_trees_component"
import { RasterComponent } from "./raster_component"
import { LogComponent } from "./log_component"
import { VectorComponent } from "./vector_component"
import { ALC_Layer, nationalForestInventoryLayer, noneWoodlandTreesLayer } from "../non_woodland"
import { addLoggingToComponents } from "./worker_logger"
import { AvailableExtent, ExtentsComponent } from "./extents_component"
import { RasterBoolCatComponent } from "./raster_bool_cat_component"
import { MapBiomasProperties } from "../props/mapbiomas"
import { ForestTypeLayer, HutanVals, IndonesiaCarbonStockLayer, KBAIndonesiaLayer, LandLayoutVals } from "../props/ind_carbon_stock"
import { IndonesiaForestLossProperties, IndonesiaForestLossYearProperties } from "../props/ind_forest_loss"
import { IndSpeciesComponent } from "./ind_species_component"
import { IndDisasterDistrictProperties, IndDisasterProperties } from "../props/ind_disaster"
import { FAOSTATProperties } from "../props/faostat"
import { CatIntersectComponent } from "./cat_intersect_component"
import { SankeyComponent } from "./sankey_component"
import { CategoricalMergeComponent } from "./categorical_merge_component"
import { CategoricalDifferenceComponent } from "./categorical_difference_component"
import { BooleanPayoffMatrixComponent } from "./boolean_payoff_matrix_component"
import { GorontaloCropProperties } from "../props/gorontalo_crops"
import { SPAMProperties } from "../props/spam"
import { PaintbrushComponent, getAnnotationsType } from "./paintbrush_component"
import { GorontaloCrops2Properties } from "../props/gorontalo_crops2"
import { GorontaloSoilsProperties } from "../props/gorontalo_soils"
import { LulcLempakeProperties } from "../props/lulc_lempake"
import { OSMLandUseComponentV2 } from "./osm_land_use_component_v2"
import { OSMPlaceComponent } from "./osm_place_component"
import { CategoricalToNumericDatasetComponent } from "./catergorical_to_numeric_component"
import { ClipComponent } from "./clip_component"
import { CreateEmptyComponent } from "./create_empty_component"
import { CreateEmptyTableComponent } from "./create_empty_table_component"
import { RenameComponent } from "./rename_component"
import { CategoricalToBooleanComponent } from "./categorical_to_boolean_component"
import { FirmsFireComponent } from "./firms_fire_component"

export interface ProjectProperties {
  extent: Extent
  zoom: number
  mask: boolean
  maskLayer: string
  maskCQL: string
  debug: boolean  
  maskShape: string | undefined
  externalLink: string | undefined
  setExtraInfo: (x: string | null) => void
}

export interface TeamComponentData {
  id: number
  name: string
  component_type: string
  settings: Record<string, unknown>
}

function normalizeVectorLayerValues(values: unknown): string[] | undefined {
  if (!Array.isArray(values)) return undefined

  const normalized = values
    .flatMap(value => String(value).split(/\s*,\s*/))
    .map(value => value.trim())
    .filter(value => value.length > 0)

  return normalized.length > 0 ? Array.from(new Set(normalized)) : undefined
}

const faostatTransform = (val: number) => {
  return val / 100
}

export function createDefaultComponents(saveMapLayer: SaveMapLayer, saveModel: SaveModel, getDatasets: getDatasets, getAnnotations: getAnnotationsType, extent: Extent, zoom: number, mask: boolean, maskLayer: string, maskCQL: string, permissions: ProjectPermissions, getCustomExpressions: getExpressionsType, debug: boolean, maskShape: string | undefined, extentList: AvailableExtent[], externalLink: string | undefined, setExtraInfo, teamName: string = '', teamComponents: TeamComponentData[] = []): BaseComponent[] {

    const projectProps: ProjectProperties = { extent, zoom, mask, maskLayer, maskCQL, debug, maskShape, externalLink, setExtraInfo }

    const restrictedComponents: BaseComponent[] = []

    // Team-defined components: appear under category = team name
    if (teamName && teamComponents?.length) {
      for (const tc of teamComponents) {
        const settings = (tc.settings || {}) as Record<string, unknown>

        if (
          tc.component_type === 'Boolean/Categorical (GeoTIFF)' &&
          settings.kind === 'bool_cat' &&
          settings.source &&
          Array.isArray(settings.options) &&
          (settings.options as { label?: string; value?: number }[]).length
        ) {
          const opts = (settings.options as { label?: string; value?: number }[]).map(o => ({
            label: String(o?.label ?? ''),
            value: Number(o?.value ?? 0),
          }))
          const compProps = {
            name: tc.name,
            sources: [{ name: tc.name, source: String(settings.source) }],
            options: opts,
          }
          restrictedComponents.push(new RasterBoolCatComponent(projectProps, compProps, teamName))
        } else if (tc.component_type === 'Continuous (GeoTIFF)') {
          if (settings.kind === 'continuous' && Array.isArray(settings.options) && (settings.options as any[]).length) {
            const opts = (settings.options as any[]).map(o => ({
              name: String(o.name ?? tc.name),
              source: String(o.source),
              area: o.area ? String(o.area) : undefined,
              unit: o.unit ? String(o.unit) : undefined,
              non_values: Array.isArray(o.non_values) ? (o.non_values as (number | string)[]).map(v => Number(v)) : undefined,
            }))
            restrictedComponents.push(new RasterComponent(projectProps, tc.name, teamName, opts))
          } else if (settings.source) {
            const option = { name: tc.name, source: String(settings.source) }
            restrictedComponents.push(new RasterComponent(projectProps, tc.name, teamName, [option]))
          }
        } else if (tc.component_type === 'Continuous/Boolean (Shapefile, GeoPackage)' && settings.kind === 'vector' && Array.isArray(settings.layers) && (settings.layers as any[]).length > 0) {
          const vectorLayers = (settings.layers as any[]).map((layer: { name?: string; type_name?: string; key?: string; output?: string; value?: string[] }) => ({
            name: String(layer.name ?? tc.name),
            source: [String(layer.type_name ?? '')],
            key: String(layer.key ?? ''),
            output: (layer.output === 'CategoricalTileGrid' || layer.output === 'NumericTileGrid' ? layer.output : 'BooleanTileGrid') as 'BooleanTileGrid' | 'CategoricalTileGrid' | 'NumericTileGrid',
            value: normalizeVectorLayerValues(layer.value),
            distributed: false,
            wms: false,
          }))
          restrictedComponents.push(new VectorComponent(tc.name, vectorLayers, projectProps, teamName))
        }
      }
    }

    // Team permissions restrict some components. Add them here.
    if (permissions.DefraHedgerows && permissions.ukEnabled) restrictedComponents.push(new HedgerowComponent(projectProps))
    if (permissions.KewSamples && permissions.ukEnabled) {
      restrictedComponents.push(new KewSamplesComponent(projectProps))
      //restrictedComponents.push(new KewHabsComponent(projectProps))
      restrictedComponents.push(new KewTreesComponent(projectProps))
      restrictedComponents.push(new RasterComponent(projectProps, 'Wakehurst Soil Carbon', 'Kew', [
        { name: 'Carbon 0-15cm' , source: 'kew:predicted_carbon_015', area: 'ha', unit: 't' },
        { name: 'Carbon 0-30cm' , source: 'kew:predicted_carbon_030', area: 'ha', unit: 't' },
        { name: 'Carbon 15-30cm' , source: 'kew:predicted_carbon_1530', area: 'ha', unit: 't' },
        { name: 'Carbon 30-45cm' , source: 'kew:predicted_carbon_3045', area: 'ha', unit: 't' },
      ]))
    }
    if (permissions.NATMAPSoil && permissions.ukEnabled) restrictedComponents.push(new NatmapSoilComponent(projectProps))

    if (permissions.ukEnabled) {
      // UK datasets
      restrictedComponents.push(new UkcehLandCoverComponent(projectProps))
      restrictedComponents.push(new LehLandCoverComponent(projectProps))
      restrictedComponents.push(new VectorComponent('Non-woodland trees', noneWoodlandTreesLayer, projectProps))
      restrictedComponents.push(new VectorComponent('National Forest Inventory', nationalForestInventoryLayer, projectProps))
      restrictedComponents.push(new VectorComponent('ALC Grade', ALC_Layer, projectProps))
      restrictedComponents.push(new IMDComponent(projectProps))
      restrictedComponents.push(new BiodiversityComponent(projectProps))
      restrictedComponents.push(new NevoLayerComponent(projectProps))
      restrictedComponents.push(new ORValComponent(projectProps))
      restrictedComponents.push(new CensusComponent(projectProps))
      restrictedComponents.push(new OSGreenSpacesComponent(projectProps))
      restrictedComponents.push(new CROMEComponent(projectProps))
      restrictedComponents.push(new ATIComponent(projectProps))
      restrictedComponents.push(new DesignationsComponent(projectProps))
    }

    if (permissions.indonesiaEnabled) {
      // Indonesia datasets
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, MapBiomasProperties, 'Indonesia'))
      restrictedComponents.push(new VectorComponent('Indonesia Carbon Stock', IndonesiaCarbonStockLayer, projectProps, 'Indonesia'))
      restrictedComponents.push(new VectorComponent('Indonesia Designations', KBAIndonesiaLayer, projectProps, 'Indonesia'))
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, LandLayoutVals, 'Indonesia'))
      restrictedComponents.push(new VectorComponent('Forest Type', ForestTypeLayer, projectProps, 'Indonesia'))
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, HutanVals, 'Indonesia'))
      restrictedComponents.push(new RasterComponent(projectProps, 'GFW Indonesia Forest 2000-2020', 'Indonesia', [
        { name: 'Forest Extent 2000', source: 'ind:potapov_forest_extent_2000', bool: 1},
        { name: 'Forest Extent 2020', source: 'ind:potapov_forest_extent_2020', bool: 1},
        { name: 'Forest Height 2000', source: 'ind:forest_height_2000', unit: 'm' },
        { name: 'Forest Height 2020', source: 'ind:forest_height_2020', unit: 'm' },
        { name: 'Forest Net Gain', source: 'ind:forest_gain', unit: 'm' },
        { name: 'Forest Net Loss', source: 'ind:forest_loss', unit: 'm' },
      ]))
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, IndonesiaForestLossProperties, 'Indonesia'))
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, IndonesiaForestLossYearProperties, 'Indonesia'))
      restrictedComponents.push(new IndSpeciesComponent(projectProps))
      restrictedComponents.push(new RasterComponent(projectProps, 'Indonesia Disaster per Regency', 'Indonesia', IndDisasterProperties, ['Type', 'Year']))
      restrictedComponents.push(new VectorComponent('Indonesia Rice Yield', [
        { name: 'Rice Yield 2018', source: ['ind:rice_yield_v2'], key: 'Beras2018', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2019', source: ['ind:rice_yield_v2'], key: 'Beras2019', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2020', source: ['ind:rice_yield_v2'], key: 'Beras2020', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2021', source: ['ind:rice_yield_v2'], key: 'Beras2021', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2022', source: ['ind:rice_yield_v2'], key: 'Beras2022', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2023', source: ['ind:rice_yield_v2'], key: 'Beras2023', output: 'NumericTileGrid', distributed: false, wms: false},
        { name: 'Rice Yield 2024', source: ['ind:rice_yield_v2'], key: 'Beras2024', output: 'NumericTileGrid', distributed: false, wms: false},
      ], projectProps, 'Indonesia'))
      //restrictedComponents.push(new VectorComponent('Gorontalo Crop Data', GorontaloCropProperties, projectProps, 'Gorontalo', ['Crop', 'Indicator', 'Year']))
      restrictedComponents.push(new RasterBoolCatComponent(projectProps, {
        name: 'LGRIP 2015',
        sources: [
          { name: 'LGRIP 2015', source: 'ind_team:LGRIP_2015' },
        ],
        options: [
          { label: 'Water', value: 0 },
          { label: 'Non-cropland', value: 1 },
          { label: 'Irrigated cropland', value: 2 },
          { label: 'Rainfed cropland', value: 3 },
        ],
      }, 'Indonesia')),
      restrictedComponents.push(new VectorComponent('Disaster per District', IndDisasterDistrictProperties, projectProps, 'Indonesia', ['Type', 'Year'])),
      
      restrictedComponents.push(new VectorComponent('BPS Gorontalo Crops', GorontaloCrops2Properties, projectProps, 'Gorontalo', ['Crop', 'Indicator', 'Year']))
      restrictedComponents.push(new VectorComponent('Gorontalo Soils', GorontaloSoilsProperties, projectProps, 'Gorontalo', ['Attribute']))
      restrictedComponents.push(new VectorComponent('Lempake LULC', LulcLempakeProperties, projectProps, 'Kalimantan', ['Classification', 'Year']))
    }


    // Freely available components here.
    const components : BaseComponent[] = [
      // Inputs (Base)
      new NumericConstantComponent(),
      new CreateEmptyComponent(projectProps),
      new CreateEmptyTableComponent(),
      new PaintbrushComponent(getAnnotations, projectProps),
      new PrecompiledModelComponent(getDatasets, projectProps),
      new ExtentsComponent(projectProps, extentList),
      new SegmentComponent(projectProps),
      new RasterComponent(projectProps, 'IUCN Rarity Weighted Richness', 'Inputs', [
        { name: '2018 RWR:All', source: 'ind:RWR2018'},
        { name: '2021 RWR:All', source: 'ind:RWR2021'},
        { name: '2022 RWR:All', source: 'ind:RWR2022'},
        { name: '2023 RWR:All', source: 'ind:RWR2023'},
        { name: '2024 RWR:All', source: 'ind:RWR2024'},
        { name: '2024 RWR:Amphibians', source: 'ind:Amphibians_RWR2024'},
        { name: '2024 RWR:Birds', source: 'ind:Birds_RWR2024'},
        { name: '2024 RWR:Mammals', source: 'ind:Mammals_RWR2024'},
        { name: '2024 RWR:Reptiles', source: 'ind:Reptiles_RWR2024'},
      ]),
      new RasterComponent(projectProps, 'IUCN Species Richness', 'Inputs', [
        { name: '2018 SR:All', source: 'ind:SR2018'},
        { name: '2021 SR:All', source: 'ind:SR2021'},
        { name: '2022 SR:All', source: 'ind:SR2022'},
        { name: '2023 SR:All', source: 'ind:SR2023'},
        { name: '2024 SR:All', source: 'ind:SR2024'},
        { name: '2024 SR:Amphibians', source: 'ind:Amphibians_SR2024'},
        { name: '2024 SR:Birds', source: 'ind:Birds_SR2024'},
        { name: '2024 SR:Mammals', source: 'ind:Mammals_SR2024'},
        { name: '2024 SR:Reptiles', source: 'ind:Reptiles_SR2024'},
      ]),
      new RasterComponent(projectProps, 'FAOSTAT Suitability Index', 'Inputs', [
        {name: 'Irrigated Oil Palm', source: 'ind:faostat_olp_irrigated', unit: '0-100 scale', option: '1km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Dryland Rice', source: 'ind:faostat_dryrice_irrigated', unit: '0-100 scale', option: '1km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Wetland Rice', source: 'ind:faostat_wetrice_irrigated', unit: '0-100 scale', option: '1km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Maize', source: 'ind:faostat_mze_irrigated', unit: '0-100 scale', option: '1km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Oil Palm', source: 'ind:faostat_olp_rainfed', unit: '0-100 scale', option: '1km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Dryland Rice', source: 'ind:faostat_dryrice_rainfed', unit: '0-100 scale', option: '1km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Wetland Rice', source: 'ind:faostat_wetrice_rainfed', unit: '0-100 scale', option: '1km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Maize', source: 'ind:faostat_mze_rainfed', unit: '0-100 scale', option: '1km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Oil Palm', source: 'ind:faostat_81-00_olp_irrigated', unit: '0-100 scale', option: '10km Irrigated 1981-2000', transform: faostatTransform},
        {name: 'Irrigated Dryland Rice', source: 'ind:faostat_81-00_dryrice_irrigated', unit: '0-100 scale', option: '10km Irrigated 1981-2000', transform: faostatTransform},
        {name: 'Irrigated Wetland Rice', source: 'ind:faostat_81-00_wetrice_irrigated', unit: '0-100 scale', option: '10km Irrigated 1981-2000', transform: faostatTransform},
        {name: 'Irrigated Maize', source: 'ind:faostat_81-00_mze_irrigated', unit: '0-100 scale', option: '10km Irrigated 1981-2000', transform: faostatTransform},
        {name: 'Irrigated Oil Palm', source: 'ind:faostat_01_20_olp_irrigated', unit: '0-100 scale', option: '10km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Dryland Rice', source: 'ind:faostat_01_20_dryrice_irrigated', unit: '0-100 scale', option: '10km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Wetland Rice', source: 'ind:faostat_01_20_wetrice_irrigated', unit: '0-100 scale', option: '10km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Irrigated Maize', source: 'ind:faostat_01_20_mze_irrigated', unit: '0-100 scale', option: '10km Irrigated 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Oil Palm', source: 'ind:faostat_81-00_olp_rainfed', unit: '0-100 scale', option: '10km Rainfed 1981-2000', transform: faostatTransform},
        {name: 'Rainfed Dryland Rice', source: 'ind:faostat_81-00_dryrice_rainfed', unit: '0-100 scale', option: '10km Rainfed 1981-2000', transform: faostatTransform},
        {name: 'Rainfed Wetland Rice', source: 'ind:faostat_81-00_wetrice_rainfed', unit: '0-100 scale', option: '10km Rainfed 1981-2000', transform: faostatTransform},
        {name: 'Rainfed Maize', source: 'ind:faostat_81-00_mze_rainfed', unit: '0-100 scale', option: '10km Rainfed 1981-2000', transform: faostatTransform},
        {name: 'Rainfed Oil Palm', source: 'ind:faostat_01-20_olp_rainfed', unit: '0-100 scale', option: '10km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Dryland Rice', source: 'ind:faostat_01-20_dryrice_rainfed', unit: '0-100 scale', option: '10km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Wetland Rice', source: 'ind:faostat_01-20_wetrice_rainfed', unit: '0-100 scale', option: '10km Rainfed 2001-2020', transform: faostatTransform},
        {name: 'Rainfed Maize', source: 'ind:faostat_01-20_mze_rainfed', unit: '0-100 scale', option: '10km Rainfed 2001-2020', transform: faostatTransform},
      ]),
      new RasterBoolCatComponent(projectProps, FAOSTATProperties),
      new RasterComponent(projectProps, 'CSIRO Biodiversity Habitat Index', 'Inputs', [
        { name: '2000 Habitat Index', source: 'ind:BILBI_P_BHIv2_Habitat_2000_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2005 Habitat Index', source: 'ind:BILBI_P_BHIv2_Habitat_2005_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2010 Habitat Index', source: 'ind:BILBI_P_BHIv2_Habitat_2010_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2015 Habitat Index', source: 'ind:BILBI_P_BHIv2_Habitat_2015_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2020 Habitat Index', source: 'ind:BILBI_P_BHIv2_Habitat_2020_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2000 Species', source: 'ind:BILBI_P_BHIv2_Species_2000_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2005 Species', source: 'ind:BILBI_P_BHIv2_Species_2005_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2010 Species', source: 'ind:BILBI_P_BHIv2_Species_2010_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2015 Species', source: 'ind:BILBI_P_BHIv2_Species_2015_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
        { name: '2020 Species', source: 'ind:BILBI_P_BHIv2_Species_2020_no_poles', trim_below: 0, non_values: [-3.4028230607370965e+38], unit: '0-1 scale'},
      ]),
      new RasterComponent(projectProps, 'GLAD Crop Cover', 'Inputs', [
        { name: 'Crop Cover 2003', source: 'ind:glad_crop_2003', bool: 1},
        { name: 'Crop Cover 2007', source: 'ind:glad_crop_2007', bool: 1},
        { name: 'Crop Cover 2011', source: 'ind:glad_crop_2011', bool: 1},
        { name: 'Crop Cover 2015', source: 'ind:glad_crop_2015', bool: 1},
        { name: 'Crop Cover 2019', source: 'ind:glad_crop_2019', bool: 1},
      ]),
      new RasterComponent(projectProps, 'SPAM', 'Global', SPAMProperties, ['Crop', 'Indicator', 'Farming Method', 'Year']),
      new FirmsFireComponent(projectProps),

      // Inputs (Mixed availability)
      new DigitalModelComponent(projectProps, permissions),

      // Inputs (Internationally available)
      new OSMLandUseComponentV2(projectProps),
      new OSMPlaceComponent(projectProps),
      new SoilComponent(projectProps),

      // Outputs
      new MapLayerComponent(saveMapLayer),
      new SaveModelOutputComponent(saveModel),

      // Properties
      new UnitComponent('Unit', projectProps, units.map((unit, idx) => ({ name: unit, id: idx }))), 
      new UnitComponent('Area', projectProps, areas.map((unit, idx) => ({ name: unit, id: idx }))),
      new UnitComponent('Type', projectProps, ["Standard Deviation", "Normal"].map((unit, idx) => ({ name: unit, id: idx }))),

      // Conversions
      new NumberToNumericDatasetComponent(),
      new NumericDatasetToNumberComponent(),
      new CategoricalComponent(),
      new CategoricalToBooleanComponent(),
      new CategoricalToNumericDatasetComponent(),
      new RenameComponent(),

      // Calculations
      new AreaComponent(),
      new DistanceMapComponent(projectProps),
      new ScaleFactorComponent(),
      new InterpolationComponent(projectProps),
      new SlopeComponent(projectProps),

      // Charts
      new BarChartComponent(),
      new DatasetChartComponent(PIE_CHART_COMPONENT_NAME, "pie", projectProps),
      new DatasetChartComponent(BAR_DISTRIBUTION_CHART_COMPONENT_NAME, "distribution", projectProps),
      new DatasetChartComponent(HISTOGRAM_CHART_COMPONENT_NAME, "hist", projectProps),
      new TableChartComponent(projectProps),
      new TableCompareChartComponent(projectProps),
      new SpiderChartComponent(projectProps),
      new SankeyComponent(projectProps),

      // Set operations
      new VariadicOpComponent('Union', '⋃', booleanDataSocket, booleanDataSocket, 'Set operations'),
      new VariadicOpComponent('Intersection', '⋂', booleanDataSocket, booleanDataSocket, 'Set operations'),
      new CatIntersectComponent(),
      new CategoricalMergeComponent(),
      new CategoricalDifferenceComponent(),
      new BooleanPayoffMatrixComponent(projectProps),
      new BinaryOpComponent('Set difference', '−', booleanDataSocket, booleanDataSocket, 'Set operations', projectProps),
      new VariadicOpComponent('Symmetric difference', 'Δ', booleanDataSocket, booleanDataSocket, 'Set operations'),
      new UnaryOpComponent('Complement', '′', 'postfix', booleanDataSocket, booleanDataSocket, 'Set operations', projectProps),

      // Arithmetic
      new MaskNumericDataComponent(),
      new ExpressionComponent(projectProps, getCustomExpressions),
      new LogComponent(projectProps),
      new BinaryOpComponent('Min', '', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Max', '', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new ClipComponent(),
      new VariadicOpComponent('Sum', '∑', numericDataSocket, numericDataSocket, 'Arithmetic', 'Sum all inputs'),
      new VariadicOpComponent('Merge', '', numericDataSocket, numericDataSocket, 'Arithmetic', 'Merge all inputs into a single dataset, NaN logic is overridden'),
      new VariadicOpComponent('Product', '∏', numericDataSocket, numericDataSocket, 'Arithmetic'),
      new BinaryOpComponent('Add', '+', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Subtract', '−', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Multiply', '×', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Divide', '÷', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Power', '^', numericNumberDataSocket, numericNumberDataSocket, 'Arithmetic', projectProps),
      new UnaryOpComponent('Negate', '−', 'prefix', numericDataSocket, numericDataSocket, 'Arithmetic', projectProps),
      new UnaryOpComponent('Reciprocal', '⁻¹', 'postfix', numericDataSocket, numericDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Less', '<', numericNumberDataSocket, booleanDataSocket, 'Arithmetic', projectProps),
      new BinaryOpComponent('Greater', '>', numericNumberDataSocket, booleanDataSocket, 'Arithmetic', projectProps),
      new ReplaceNaNComponent(projectProps),

      // DEBUG TOOLS
      new CellAreaComponent(),
      new RescaleComponent(),

    ]

    let allcomps = components.concat(restrictedComponents)
    
    // Sort components into specific order: Outputs, Inputs, Indonesia, Kalimantan, Gorontalo, Global, team category, then everything else
    const categoryOrder = ['Outputs', 'Inputs', 'Indonesia', 'Kalimantan', 'Gorontalo', 'Global', teamName].filter(Boolean)
    allcomps.sort((a, b) => {
      const categoryA = a.category || ''
      const categoryB = b.category || ''
      
      const indexA = categoryOrder.indexOf(categoryA)
      const indexB = categoryOrder.indexOf(categoryB)
      
      // If both categories are in the order list, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }
      // If only A is in the order list, A comes first
      if (indexA !== -1) {
        return -1
      }
      // If only B is in the order list, B comes first
      if (indexB !== -1) {
        return 1
      }
      // If neither is in the order list, maintain their relative order
      return 0
    })
    
    if (debug) console.log('Debug mode has been enabled. Worker execution will be logged and nodes will be highlighted.')


    // Wrap components with logging before returning them
    return debug ? addLoggingToComponents(allcomps) : allcomps;
}
